// src/components/NotificationsPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bell, Heart, MessageCircle, UserPlus, Mail, Check, User, Clock, CheckCircle, Trash2, Filter, X, UserCheck,
  FileText, Award, Star, HeartHandshake, TrendingUp, Sparkles, Coins, AlertTriangle, Megaphone, RotateCcw,
} from 'lucide-react';
import { useSocket } from './Context';
import { useSmartBack } from '../utils/useSmartBack.js';
import './NotificationsPage.css';

import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
const NotificationsPage = () => {
  const { t, i18n } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'like', 'comment', 'follow', 'message'
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();
  const goBack = useSmartBack('/feed');
  const { socket } = useSocket();

  useEffect(() => {
    loadNotifications();
    
    // Listen for real-time notifications if socket exists
    if (socket) {
      const handleNewNotification = (notification) => {
        console.log('📨 New real-time notification:', notification);
        setNotifications(prev => [notification, ...prev]);
        setUnreadCount(prev => prev + 1);
      };

      socket.on('new-notification', handleNewNotification);

      return () => {
        socket.off('new-notification', handleNewNotification);
      };
    }
  }, [socket]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${GREENWICH_BACKEND_URL}/api/notifications`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        
        // Calculate unread count
        const unread = (data.notifications || []).filter(n => !n.isRead).length;
        setUnreadCount(unread);
      } else {
        console.error('Failed to load notifications');
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
      // Fallback data for testing
      setNotifications(getMockNotifications());
      setUnreadCount(3);
    } finally {
      setLoading(false);
    }
  };

  // Mock data for testing if API is not available
  const getMockNotifications = () => {
    return [
      {
        _id: '1',
        type: 'like',
        sender: { name: 'John Doe', username: 'johndoe', profileImage: '/nkarner/default-avatar.jpg' },
        isRead: false,
        createdAt: new Date(Date.now() - 1000 * 60 * 5).toISOString(), // 5 minutes ago
        post: { _id: 'post1' }
      },
      {
        _id: '2',
        type: 'comment',
        sender: { name: 'Jane Smith', username: 'janesmith', profileImage: '/nkarner/default-avatar.jpg' },
        isRead: true,
        createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(), // 30 minutes ago
        comment: 'Great post!',
        post: { _id: 'post2' }
      },
      {
        _id: '3',
        type: 'follow',
        sender: { name: 'Alex Johnson', username: 'alexj', profileImage: '/nkarner/default-avatar.jpg' },
        isRead: false,
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(), // 2 hours ago
      },
      {
        _id: '4',
        type: 'mention',
        sender: { name: 'Sarah Wilson', username: 'sarahw', profileImage: '/nkarner/default-avatar.jpg' },
        isRead: true,
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(), // 1 day ago
        post: { _id: 'post3' }
      },
      {
        _id: '5',
        type: 'message',
        sender: { name: 'Mike Brown', username: 'mikeb', profileImage: '/nkarner/default-avatar.jpg' },
        isRead: false,
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(), // 2 days ago
      }
    ];
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${GREENWICH_BACKEND_URL}/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Update local state
        setNotifications(prev => 
          prev.map(notif => 
            notif._id === notificationId ? { ...notif, isRead: true } : notif
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
        
        // Emit socket event if available
        if (socket) {
          socket.emit('notification-read', { notificationId });
        }
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Fallback: update locally
      setNotifications(prev => 
        prev.map(notif => 
          notif._id === notificationId ? { ...notif, isRead: true } : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${GREENWICH_BACKEND_URL}/api/notifications/read-all`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setNotifications(prev => 
          prev.map(notif => ({ ...notif, isRead: true }))
        );
        setUnreadCount(0);
        
        // Emit socket event if available
        if (socket) {
          socket.emit('all-notifications-read');
        }
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      // Fallback: update locally
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, isRead: true }))
      );
      setUnreadCount(0);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      setDeletingId(notificationId);
      const token = localStorage.getItem('token');
      const response = await fetch(`${GREENWICH_BACKEND_URL}/api/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Update local state
        setNotifications(prev => prev.filter(notif => notif._id !== notificationId));
        // Reduce unread count if notification was unread
        const wasUnread = notifications.find(n => n._id === notificationId)?.isRead === false;
        if (wasUnread) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      // Fallback: remove locally
      setNotifications(prev => prev.filter(notif => notif._id !== notificationId));
    } finally {
      setDeletingId(null);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.isRead) {
      await markAsRead(notification._id);
    }

    // Navigate based on notification type — kept in sync with NotificationModal.jsx's
    // handleItemClick, which is the more complete/original implementation of this routing.
    const type = notification.type;
    if (type === 'post_deleted') return;
    if (['like', 'comment', 'mention'].includes(type)) {
      navigate(notification.post ? `/post/${notification.post._id || notification.post}` : '/feed');
    } else if (type === 'follow_accepted' || type === 'follow') {
      const sid = notification.sender?._id || notification.sender?.id;
      navigate(sid ? `/profile/${sid}` : '/feed');
    } else if (type === 'follow_request') {
      navigate('/follow-requests');
    } else if (type === 'message') {
      navigate('/messages');
    } else if (type === 'verification') {
      navigate('/organization-account-profile');
    } else if (type === 'application_submitted') {
      navigate('/programs/my-submissions');
    } else if (type === 'application_response' || type === 'application_attended' || type === 'certificate_issued' || type === 'buddy_certificate_earned') {
      navigate('/dashboard', { state: { section: type === 'application_response' || type === 'application_attended' ? 'applications' : 'certificates' } });
    } else if (type === 'org_review_received') {
      navigate('/organization-account-profile');
    } else if (type === 'join_request' || type === 'join_request_response') {
      const groupId = notification.metadata?.groupId || notification.group;
      navigate(groupId ? `/community/group/${groupId}` : '/my-communities');
    } else if (type === 'buddy_mentor_application') {
      navigate('/admin/buddy-mentors');
    } else if (typeof type === 'string' && type.startsWith('buddy_')) {
      navigate('/buddy');
    } else if (type === 'level_up') {
      navigate('/levels');
    } else if (type === 'project_match') {
      navigate('/programs');
    } else if (type === 'greencoin_reward') {
      navigate('/greencoins');
    } else if (type === 'org_balance_low') {
      navigate('/organization/greencoins');
    } else if (type === 'support_contact_request') {
      navigate('/admin/inbox');
    } else if (type === 'pro_interest' || type === 'greencoin_interest') {
      navigate('/admin/users');
    } else if (type === 'refund_requested') {
      navigate('/admin/refunds');
    } else if (type === 'refund_decided') {
      navigate('/settings');
    } else if (type === 'admin_announcement') {
      const link = notification.metadata?.link;
      const programId = notification.metadata?.programId;
      if (link && link.startsWith('/')) navigate(link);
      else if (link) window.location.assign(link);
      else if (programId) navigate(`/programs?program=${programId}`);
      else navigate('/feed');
    } else {
      navigate('/feed');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'like':
        return <Heart className="text-red-500" size={24} />;
      case 'comment':
        return <MessageCircle className="text-blue-500" size={24} />;
      case 'follow':
      case 'follow_accepted':
      case 'follow_request':
        return <UserPlus className="text-green-500" size={24} />;
      case 'message':
        return <Mail className="text-purple-500" size={24} />;
      case 'verification':
        return <Check className="text-green-500" size={24} />;
      case 'mention':
        return <User className="text-orange-500" size={24} />;
      case 'application_submitted':
      case 'application_response':
      case 'application_attended':
        return <FileText className="text-blue-500" size={24} />;
      case 'certificate_issued':
      case 'buddy_certificate_earned':
        return <Award className="text-amber-500" size={24} />;
      case 'org_review_received':
        return <Star className="text-amber-500" size={24} />;
      case 'buddy_mentor_application':
      case 'buddy_mentor_approved':
      case 'buddy_mentor_rejected':
      case 'buddy_request':
      case 'buddy_request_accepted':
      case 'buddy_request_declined':
      case 'buddy_mentorship_ended':
        return <HeartHandshake className="text-pink-500" size={24} />;
      case 'level_up':
        return <TrendingUp className="text-green-500" size={24} />;
      case 'project_match':
        return <Sparkles className="text-amber-500" size={24} />;
      case 'greencoin_reward':
        return <Coins className="text-amber-500" size={24} />;
      case 'org_balance_low':
        return <AlertTriangle className="text-red-500" size={24} />;
      case 'admin_announcement':
        return <Megaphone className="text-blue-500" size={24} />;
      case 'support_contact_request':
      case 'pro_interest':
      case 'greencoin_interest':
        return <Mail className="text-purple-500" size={24} />;
      case 'refund_requested':
      case 'refund_decided':
        return <RotateCcw className="text-orange-500" size={24} />;
      default:
        return <Bell className="text-gray-500" size={24} />;
    }
  };

  const getNotificationMessage = (notification) => {
    const senderName = notification.sender?.name || t('pages.notifications.someone');
    
    switch (notification.type) {
      case 'like':
        return t('pages.notifications.msgLike', { name: senderName });
      case 'comment':
        return t('pages.notifications.msgComment', { name: senderName });
      case 'follow':
        return t('pages.notifications.msgFollow', { name: senderName });
      case 'follow_request':
        return t('pages.notifications.msgFollowReq', { name: senderName });
      case 'message':
        return t('pages.notifications.msgMessage', { name: senderName });
      case 'verification':
        return t('pages.notifications.msgVerify');
      case 'mention':
        return t('pages.notifications.msgMention', { name: senderName });
      default:
        return notification.message || t('pages.notifications.msgNew');
    }
  };

  // Bare-URL detector — pulls a pasted link (e.g. a Facebook post URL) out of
  // a raw notification body so it renders as a proper chip instead of sitting
  // inline as unstyled plain text next to the rest of the message.
  const URL_RE = /(https?:\/\/[^\s]+)/i;
  const splitTextAndLink = (raw) => {
    if (!raw) return { text: '', link: null };
    const match = raw.match(URL_RE);
    if (!match) return { text: raw, link: null };
    const link = match[0].replace(/[),.;]+$/, '');
    const text = (raw.slice(0, match.index) + raw.slice(match.index + match[0].length)).replace(/\s{2,}/g, ' ').trim();
    return { text, link };
  };
  const truncateText = (str, max = 160) => (str && str.length > max ? str.slice(0, max).trimEnd() + '…' : str);
  const linkLabel = (link) => { try { return new URL(link).hostname.replace(/^www\./, ''); } catch { return link; } };
  const clampStyle = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' };

  const DEFAULT_AVATAR = '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';

  const getImageUrl = (imagePath) => {
    if (!imagePath || imagePath === 'null' || imagePath === 'undefined') return DEFAULT_AVATAR;
    if (imagePath.startsWith('http')) return imagePath;
    if (imagePath.includes('uploads') || imagePath.includes('nkarner')) {
      const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
      return `${GREENWICH_BACKEND_URL}${cleanPath}`;
    }
    return DEFAULT_AVATAR;
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('pages.notifications.justNow');
    if (diffMins < 60) return t('pages.notifications.timeAgo', { n: diffMins });
    if (diffHours < 24) return t('pages.notifications.hourAgo', { n: diffHours });
    if (diffDays < 7) return t('pages.notifications.dayAgo', { n: diffDays });
    return new Intl.DateTimeFormat(i18n.language || undefined, {
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  const filteredNotifications = notifications.filter(notification => {
    // Read/unread filter
    if (filter === 'unread' && notification.isRead) return false;
    if (filter === 'read' && !notification.isRead) return false;
    
    // Type filter
    if (typeFilter !== 'all' && notification.type !== typeFilter) return false;
    
    return true;
  });

  // Group notifications by date
  const groupNotificationsByDate = () => {
    const groups = {};
    filteredNotifications.forEach(notification => {
      const date = new Date(notification.createdAt);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      let dateKey;
      if (date.toDateString() === today.toDateString()) {
        dateKey = t('pages.notifications.today');
      } else if (date.toDateString() === yesterday.toDateString()) {
        dateKey = t('pages.notifications.yesterday');
      } else {
        // Same Intl.DateTimeFormat(i18n.language, ...) convention used
        // elsewhere (utils/deadline.js) — was previously a hand-rolled
        // hy/en-only ternary that fell back to English for any other
        // active language, so this and the translated Today/Yesterday
        // labels above could show mixed languages in the same list.
        dateKey = new Intl.DateTimeFormat(i18n.language || undefined, {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }).format(date);
      }
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(notification);
    });
    
    return groups;
  };

  const notificationGroups = groupNotificationsByDate();

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div
          className="animate-spin rounded-full h-12 w-12 border-b-2"
          style={{ borderBottomColor: '#32614d' }}
        ></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex flex-wrap justify-between items-center" style={{ gap: 10 }}>
            <div className="flex items-center gap-3">
              <button
                onClick={goBack}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
              <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
                <Bell className="text-blue-500" size={28} />
                <h1 className="text-2xl font-bold">{t('pages.notifications.title')}</h1>
                {unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-sm font-medium px-2 py-1 rounded-full">
                    {t('pages.notifications.newBadge', { count: unreadCount })}
                  </span>
                )}
              </div>
            </div>

            {notifications.length > 0 && unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
              >
                <CheckCircle size={18} />
                {t('pages.notifications.markAllRead')}
              </button>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-xl font-bold text-blue-600">{notifications.length}</div>
              <div className="text-sm text-gray-600">{t('pages.notifications.total')}</div>
            </div>
            <div className="bg-red-50 p-3 rounded-lg">
              <div className="text-xl font-bold text-red-600">{unreadCount}</div>
              <div className="text-sm text-gray-600">{t('pages.notifications.unread')}</div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="text-xl font-bold text-green-600">
                {notifications.filter(n => n.type === 'like').length}
              </div>
              <div className="text-sm text-gray-600">{t('pages.notifications.likes')}</div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="text-xl font-bold text-purple-600">
                {notifications.filter(n => n.type === 'follow').length}
              </div>
              <div className="text-sm text-gray-600">{t('pages.notifications.follows')}</div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mt-4">
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-gray-500" />
              <span className="text-gray-600 font-medium">{t('pages.notifications.filter')}</span>
            </div>
            
            {/* Read status filter */}
            <div className="flex gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  filter === 'all' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('pages.notifications.all')}
              </button>
              <button
                onClick={() => setFilter('unread')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  filter === 'unread' 
                    ? 'bg-red-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('pages.notifications.unreadTab')}
              </button>
              <button
                onClick={() => setFilter('read')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  filter === 'read' 
                    ? 'bg-green-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('pages.notifications.readTab')}
              </button>
            </div>

            {/* Type filter */}
            <div className="flex gap-2">
              <button
                onClick={() => setTypeFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  typeFilter === 'all' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('pages.notifications.allTypes')}
              </button>
              <button
                onClick={() => setTypeFilter('like')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  typeFilter === 'like' 
                    ? 'bg-red-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('pages.notifications.likes')}
              </button>
              <button
                onClick={() => setTypeFilter('comment')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  typeFilter === 'comment' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('pages.notifications.comments')}
              </button>
              <button
                onClick={() => setTypeFilter('follow')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  typeFilter === 'follow' 
                    ? 'bg-green-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('pages.notifications.follows')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Notifications List */}
        {filteredNotifications.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full shadow mb-6">
              <Bell className="text-gray-400" size={40} />
            </div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">{t('pages.notifications.noneTitle')}</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              {filter === 'unread' 
                ? t('pages.notifications.noneUnread')
                : t('pages.notifications.noneDefault')}
            </p>
            <button
              onClick={() => {
                setFilter('all');
                setTypeFilter('all');
              }}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              {t('pages.notifications.showAll')}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(notificationGroups).map(([date, groupNotifications]) => (
              <div key={date}>
                <h2 className="text-lg font-semibold text-gray-700 mb-3">{date}</h2>
                <div className="space-y-3">
                  {groupNotifications.map((notification) => (
                    <div
                      key={notification._id}
                      className={`bg-white border rounded-xl p-4 hover:shadow-lg transition-all duration-200 cursor-pointer ${
                        !notification.isRead ? 'border-l-4 border-l-blue-500 bg-blue-50/30' : ''
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex items-start gap-4">
                        {/* Icon */}
                        <div className="mt-1">
                          {getNotificationIcon(notification.type)}
                        </div>

                        {/* Content */}
                        <div className="flex-1">
                          <div className="flex justify-between">
                            <div>
                              {(() => {
                                // Every notification renders its body through this same
                                // clamped treatment — including ones whose message is a
                                // raw backend-supplied string (getNotificationMessage's
                                // fallback) rather than one of the templated types above,
                                // so a long raw post/message body can't blow out the card.
                                const raw = getNotificationMessage(notification);
                                const { text, link } = splitTextAndLink(raw);
                                return (
                                  <>
                                    <div className={`font-medium ${!notification.isRead ? 'text-gray-900' : 'text-gray-700'}`} style={clampStyle}>
                                      {truncateText(text || raw, 160)}
                                    </div>
                                    {link && (
                                      <a
                                        href={link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="mt-1 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-blue-600 text-xs font-medium hover:bg-gray-200"
                                        style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                      >
                                        🔗 {linkLabel(link)}
                                      </a>
                                    )}
                                  </>
                                );
                              })()}

                              {notification.comment && (
                                <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                                  <p className="text-gray-600 text-sm" style={clampStyle}>"{truncateText(notification.comment, 160)}"</p>
                                </div>
                              )}
                            </div>

                            <div className="flex flex-col items-end gap-2">
                              <span className="text-sm text-gray-500 flex items-center gap-1">
                                <Clock size={14} />
                                {formatTime(notification.createdAt)}
                              </span>
                              
                              <div className="flex gap-2">
                                {!notification.isRead && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      markAsRead(notification._id);
                                    }}
                                    className="text-sm text-blue-500 hover:text-blue-700 flex items-center gap-1"
                                  >
                                    <UserCheck size={14} />
                                    {t('pages.notifications.markRead')}
                                  </button>
                                )}
                                
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteNotification(notification._id);
                                  }}
                                  disabled={deletingId === notification._id}
                                  className="text-sm text-gray-400 hover:text-red-500 disabled:opacity-50"
                                  title={t('pages.notifications.deleteTitle')}
                                >
                                  {deletingId === notification._id ? (
                                    <span className="animate-pulse">{t('pages.notifications.deleting')}</span>
                                  ) : (
                                    <Trash2 size={16} />
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                          
                          {/* Sender info */}
                          {notification.sender && (
                            <div className="flex items-center gap-2 mt-3">
                              <img
                                src={getImageUrl(notification.sender.profileImage)}
                                alt={notification.sender.name}
                                className="w-6 h-6 rounded-full"
                                style={{ objectFit: 'cover' }}
                                onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_AVATAR; }}
                              />
                              <span className="text-sm text-gray-600">
                                @{notification.sender.username || notification.sender.name}
                              </span>
                              {notification.sender.organizationName && (
                                <span className="text-sm text-gray-500">
                                  • {notification.sender.organizationName}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        {filteredNotifications.length > 0 && (
          <div className="mt-8 pt-6 border-t text-center text-gray-500 text-sm">
            {t('pages.notifications.footer', { shown: filteredNotifications.length, total: notifications.length })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
